import { useQuery } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";
import type React from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { useSuperTokensUserId } from "@/authentication/AuthenticationStateProvider";
import { AutoTranslationLanguageSettingModal } from "@/components/AutoTranslation/AutoTranslationLanguageSettingModal";
import { AutoTranslationUserLanguageSupportedModal } from "@/components/AutoTranslation/AutoTranslationUserLanguageSupportedModal";
import { LOCAL_STORAGE_KEYS } from "@/helpers/storage";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { getLocalStorageValue, updateLocalStorage } from "@/hooks/useLocalStorage";
import { useConfig } from "@/providers/config/useConfig";
import { useGeneralQueries } from "@/queries/general/queryOptions";
import { selfAccountMutations } from "@/queries/self-account/mutations";
import { useSelfAccountQueries } from "@/queries/self-account/queryOptions";

import { canViewAlertManagementUpdate, canViewPortfolioTicketUpdate, getProductUpdateActionPath } from "./helpers";
import {
  ALERT_MANAGEMENT_UPDATE_ID,
  AUTO_TRANSLATIONS_UPDATE_ID,
  DASHBOARD_CONTEXTUAL_HELP_UPDATE_ID,
  isProductUpdateUnread,
  PORTFOLIO_TICKET_FILTERS_UPDATE_ID,
  PRODUCT_UPDATES,
  type ProductUpdate,
} from "./productUpdates";
import { ProductUpdatesDrawer } from "./ProductUpdatesDrawer";
import { getBaseLanguageCode, shouldShowSupportedLanguagePrompt } from "./supportedLanguagePrompt";

const browserLanguage = getBaseLanguageCode(window.navigator.language);
const AUTOMATIC_PROMPT_DELAY = 2000;

interface ProductUpdatesContextValue {
  hasUnreadUpdates: boolean;
  openProductUpdates: () => void;
}

const ProductUpdatesContext = createContext<ProductUpdatesContextValue | null>(null);

export function useProductUpdates(): ProductUpdatesContextValue {
  const context = useContext(ProductUpdatesContext);
  if (!context) {
    throw new Error("useProductUpdates must be used within a ProductUpdatesProvider");
  }

  return context;
}

export function ProductUpdatesProvider({ children }: { children: React.ReactNode }): React.ReactNode {
  const superTokensUserId = useSuperTokensUserId();

  if (!superTokensUserId) {
    return (
      <ProductUpdatesContext value={{ hasUnreadUpdates: false, openProductUpdates: () => undefined }}>
        {children}
      </ProductUpdatesContext>
    );
  }

  return <ResolvedProductUpdatesProvider userId={superTokensUserId}>{children}</ResolvedProductUpdatesProvider>;
}

function ResolvedProductUpdatesProvider({
  children,
  userId,
}: {
  children: React.ReactNode;
  userId: string;
}): React.ReactNode {
  const sessionUser = useSessionUser();
  const navigate = useNavigate();
  const { slug: projectSlug } = useParams<{ slug?: string }>();
  const postHog = usePostHog();
  const generalQueries = useGeneralQueries();
  const selfAccountQueries = useSelfAccountQueries();
  const { data: linkedProjects } = useQuery(selfAccountQueries.listLinkedProjects());
  const isAlertManagementUpdateAvailable = canViewAlertManagementUpdate(linkedProjects ?? []);
  const isPortfolioTicketUpdateAvailable = canViewPortfolioTicketUpdate(linkedProjects ?? []);
  const updateSelfLanguageMutation = selfAccountMutations.useUpdateLanguage();
  const [isDrawerOpened, drawerHandlers] = useBool(false);
  const [isLanguageSettingsOpened, languageSettingsHandlers] = useBool(false);
  const [isSupportedLanguagePromptOpened, supportedLanguagePromptHandlers] = useBool(false);
  const isContextualHelpAvailable = useConfig("isDashboardContextualHelpAvailable");
  const updates = useMemo(
    () =>
      PRODUCT_UPDATES.filter(
        (update) =>
          (update.id !== DASHBOARD_CONTEXTUAL_HELP_UPDATE_ID || isContextualHelpAvailable) &&
          (update.id !== ALERT_MANAGEMENT_UPDATE_ID || isAlertManagementUpdateAvailable) &&
          (update.id !== PORTFOLIO_TICKET_FILTERS_UPDATE_ID || isPortfolioTicketUpdateAvailable),
      ),
    [isAlertManagementUpdateAvailable, isContextualHelpAvailable, isPortfolioTicketUpdateAvailable],
  );
  const [selectedUpdateId, setSelectedUpdateId] = useState(updates[0].id);
  const storageKey = LOCAL_STORAGE_KEYS.PRODUCT_UPDATES_READ(userId);
  const [readUpdateIds, setReadUpdateIds] = useState<string[]>(() => getInitialReadUpdateIds(storageKey));
  const handledAutomaticPromptRef = useRef(false);
  const { data: autoTranslationLanguages = [] } = useQuery(
    generalQueries.languages({
      subset: "autoTranslation",
    }),
  );

  const readUpdateIdSet = useMemo(() => new Set(readUpdateIds), [readUpdateIds]);
  const hasUnreadUpdates = updates.some((update) => isProductUpdateUnread(update, readUpdateIdSet));
  const supportedBrowserLanguage = autoTranslationLanguages.find((language) => language.id === browserLanguage);

  const markUpdateRead = useCallback(
    (updateId: string) => {
      setReadUpdateIds((currentReadUpdateIds) => {
        if (currentReadUpdateIds.includes(updateId)) {
          return currentReadUpdateIds;
        }

        const nextReadUpdateIds = [...currentReadUpdateIds, updateId];
        updateLocalStorage(storageKey, nextReadUpdateIds);

        return nextReadUpdateIds;
      });

      if (updateId === AUTO_TRANSLATIONS_UPDATE_ID) {
        updateLocalStorage(LOCAL_STORAGE_KEYS.NEW_FEATURE.AUTO_TRANSLATION_FEATURE, "true");
      }
    },
    [storageKey],
  );

  const showUpdate = useCallback(
    (update: ProductUpdate, source: "automatic" | "user-menu" | "update-list") => {
      setSelectedUpdateId(update.id);
      markUpdateRead(update.id);
      drawerHandlers.setTrue();
      postHog.capture("viewed_product_update", {
        productUpdateId: update.id,
        source,
      });
    },
    [drawerHandlers, markUpdateRead, postHog],
  );

  const openProductUpdates = useCallback(() => {
    const update = updates.find((item) => isProductUpdateUnread(item, readUpdateIdSet)) ?? updates[0];
    showUpdate(update, "user-menu");
    postHog.capture("opened_product_updates", {
      source: "user-menu",
    });
  }, [postHog, readUpdateIdSet, showUpdate, updates]);

  const handleSelectUpdate = useCallback(
    (updateId: string) => {
      const update = updates.find((item) => item.id === updateId);
      if (update) {
        showUpdate(update, "update-list");
      }
    },
    [showUpdate, updates],
  );

  const handleClickAction = useCallback(
    (update: ProductUpdate) => {
      if (!update.action) {
        return;
      }

      postHog.capture("clicked_product_update_cta", {
        action: update.action.type,
        productUpdateId: update.id,
      });

      const actionPath = getProductUpdateActionPath(update.action.type, projectSlug);
      if (actionPath) {
        drawerHandlers.setFalse();
        void navigate(actionPath);
      } else if (update.action.type === "review-auto-translation-settings") {
        drawerHandlers.setFalse();
        languageSettingsHandlers.setTrue();
      }
    },
    [drawerHandlers, languageSettingsHandlers, navigate, postHog, projectSlug],
  );

  const handleSelectLanguage = useCallback(
    async (language: (typeof autoTranslationLanguages)[number]) => {
      await updateSelfLanguageMutation.mutateAsync({
        uiLanguageId: sessionUser.uiLanguage.id,
        autoTranslationLanguageId: language.id,
      });
    },
    [sessionUser.uiLanguage.id, updateSelfLanguageMutation],
  );

  const handleSupportedLanguagePromptHandled = useCallback(() => {
    if (supportedBrowserLanguage) {
      updateLocalStorage(
        LOCAL_STORAGE_KEYS.NEW_FEATURE.AUTO_TRANSLATION_USER_LANGUAGE_SUPPORTED,
        supportedBrowserLanguage.id,
      );
    }
  }, [supportedBrowserLanguage]);

  useEffect(() => {
    updateLocalStorage(storageKey, readUpdateIds);
  }, [readUpdateIds, storageKey]);

  useEffect(() => {
    if (autoTranslationLanguages.length === 0 || handledAutomaticPromptRef.current || !sessionUser.onboardedAt) {
      return;
    }

    const handledSupportedLanguageId = getLocalStorageValue(
      LOCAL_STORAGE_KEYS.NEW_FEATURE.AUTO_TRANSLATION_USER_LANGUAGE_SUPPORTED,
      "",
    );
    const showSupportedLanguagePrompt = shouldShowSupportedLanguagePrompt({
      isOnboarded: Boolean(sessionUser.onboardedAt),
      supportedLanguage: supportedBrowserLanguage,
      selectedLanguageId: sessionUser.autoTranslationLanguage?.id,
      handledLanguageId: handledSupportedLanguageId,
    });
    const promotedUpdate = sessionUser.role.isResidentType
      ? undefined
      : updates.find((update) => update.autoOpen && isProductUpdateUnread(update, readUpdateIdSet));

    const timer = window.setTimeout(() => {
      handledAutomaticPromptRef.current = true;

      if (showSupportedLanguagePrompt) {
        supportedLanguagePromptHandlers.setTrue();
        updateLocalStorage(LOCAL_STORAGE_KEYS.NEW_FEATURE.AUTO_TRANSLATION_FEATURE, "true");

        return;
      }

      if (promotedUpdate && !window.AOP_E2E_DISABLE_PRODUCT_UPDATES_AUTO_OPEN) {
        showUpdate(promotedUpdate, "automatic");
        postHog.capture("opened_product_updates", {
          productUpdateId: promotedUpdate.id,
          source: "automatic",
        });
      }
    }, AUTOMATIC_PROMPT_DELAY);

    return () => window.clearTimeout(timer);
  }, [
    autoTranslationLanguages.length,
    postHog,
    readUpdateIdSet,
    sessionUser.autoTranslationLanguage?.id,
    sessionUser.onboardedAt,
    sessionUser.role.isResidentType,
    showUpdate,
    supportedBrowserLanguage,
    supportedLanguagePromptHandlers,
    updates,
  ]);

  const contextValue = useMemo(
    () => ({
      hasUnreadUpdates,
      openProductUpdates,
    }),
    [hasUnreadUpdates, openProductUpdates],
  );

  return (
    <ProductUpdatesContext value={contextValue}>
      {children}
      <ProductUpdatesDrawer
        isOpened={isDrawerOpened}
        onOpenChange={drawerHandlers.set}
        updates={updates}
        selectedUpdateId={selectedUpdateId}
        readUpdateIds={readUpdateIdSet}
        onSelectUpdate={handleSelectUpdate}
        onClickAction={handleClickAction}
      />
      <AutoTranslationLanguageSettingModal
        isOpened={isLanguageSettingsOpened}
        onOpenChange={languageSettingsHandlers.set}
        languages={autoTranslationLanguages}
        selectedLanguage={sessionUser.autoTranslationLanguage ?? sessionUser.uiLanguage}
        onSelectLanguage={handleSelectLanguage}
        isSubmitting={updateSelfLanguageMutation.isPending}
      />
      <AutoTranslationUserLanguageSupportedModal
        isOpened={isSupportedLanguagePromptOpened}
        onOpenChange={supportedLanguagePromptHandlers.set}
        userLanguage={supportedBrowserLanguage}
        onHandled={handleSupportedLanguagePromptHandled}
      />
    </ProductUpdatesContext>
  );
}

function getInitialReadUpdateIds(storageKey: string): string[] {
  const storedReadUpdateIds = getLocalStorageValue<string[]>(storageKey, []);
  const hasSeenLegacyAutoTranslationModal =
    getLocalStorageValue(LOCAL_STORAGE_KEYS.NEW_FEATURE.AUTO_TRANSLATION_FEATURE, "false") !== "false";

  if (hasSeenLegacyAutoTranslationModal && !storedReadUpdateIds.includes(AUTO_TRANSLATIONS_UPDATE_ID)) {
    return [...storedReadUpdateIds, AUTO_TRANSLATIONS_UPDATE_ID];
  }

  return storedReadUpdateIds;
}
